export type CprMode = 'straightened' | 'stretched' | 'avc';
export type CprCrossSectionImageMode = 'legacy' | 'server';

export type ImageLayerKey =
  | 'base'
  | 'lumen'
  | 'lumenoutline'
  | 'plaque'
  | 'plaqueoutline'
  | 'fai'
  | 'aorta'
  | 'avc';

export type ImageLayerPaths = {
  base: string;
  lumen?: string;
  lumenoutline?: string;
  plaque?: string;
  plaqueoutline?: string;
  fai?: string;
  aorta?: string;
  avc?: string;
};

export type ImageTileRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageLayerTransform = {
  tileRect?: ImageTileRect;
  outputWidth?: number;
  outputHeight?: number;
  flipY?: boolean;
  compression?: string;
  uncompressedBytes?: number;
};

export type CprBaseImageTransform = ImageLayerTransform & {
  dtype?: string;
  huMin?: number;
  huMax?: number;
};

export type BranchVolumeSliceTransform = {
  frameIndex: number;
  width: number;
  height: number;
  frameStrideBytes: number;
  compression?: string;
  uncompressedBytes?: number;
  dtype?: string;
  huMin?: number;
  huMax?: number;
};

export type ImageLayerLabelValues = Partial<
  Record<Exclude<ImageLayerKey, 'base'>, number[]>
>;

export type CprCenterlinePathPoint = {
  index: number;
  x: number;
  y: number;
  distanceMm?: number;
  sourceCenterlineIndex?: number;
  position?: [number, number, number];
  normal?: [number, number, number];
};

export type CprFrameViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type MockCenterlinePoint = [number, number, number];

export type CprOverlayPoint = {
  x: number;
  y: number;
};

export type CprOverlayPerpendicularLine = {
  index: number;
  start: CprOverlayPoint;
  end: CprOverlayPoint;
};

export type CprOverlayLine = {
  key?: string;
  type?: string;
  closed?: boolean;
  points: CprOverlayPoint[];
};

export type CprOverlayIndexBand = {
  key?: string;
  indexStart: number;
  indexEnd: number;
  polygon: CprOverlayPoint[];
  boundaryLines?: CprOverlayPerpendicularLine[];
};

export type CprOverlayGeometry = {
  origin?: 'top-left';
  coordinateSpace?: 'image';
  perpendicularLines?: CprOverlayPerpendicularLine[];
  indexBands?: CprOverlayIndexBand[];
  outlineLines?: CprOverlayLine[];
};

export type CprAngleFrame = {
  angleDeg: number;
  width: number;
  height: number;
  layers: ImageLayerPaths;
  labelPath?: string;
  labelFlipY?: boolean;
  labelCompression?: string;
  labelUncompressedBytes?: number;
  localIndexStart?: number;
  localIndexEnd?: number;
  clientIndexStart?: number;
  clientIndexEnd?: number;
  sourceIndexStart?: number;
  sourceIndexEnd?: number;
  frontPaddingRows?: number;
  backPaddingRows?: number;
  validImageRowStart?: number;
  validImageRowEnd?: number;
  rowCenterlineIndex?: number[];
  clientIndexByRow?: number[];
  rowByClientIndex?: number[];
  pathClientIndex?: number[];
  rowDistanceMm?: number[];
  pathDistanceMm?: number[];
  centerlinePath: CprCenterlinePathPoint[];
  defaultViewBox?: CprFrameViewBox;
  overlayGeometry?: CprOverlayGeometry;
};

export type CprFrameGeometry = {
  width?: number;
  height?: number;
  centerlinePath: CprCenterlinePathPoint[];
  defaultViewBox?: CprFrameViewBox;
  overlayGeometry?: CprOverlayGeometry;
};

export type CprFrameGeometryProviderInput = {
  branchId: string;
  mode: CprMode;
  width: number;
  height: number;
  angleDeg: number;
  pointCount: number;
  centerlinePoints?: MockCenterlinePoint[];
};

export type CprImageResource = {
  mode: CprMode;
  angleFrames: CprAngleFrame[];
};

const RASTER_OVERLAY_LAYER_KEYS: Exclude<ImageLayerKey, 'base'>[] = [
  'lumen',
  'lumenoutline',
  'plaque',
  'plaqueoutline',
  'fai',
  'aorta',
  'avc',
];

export function getAllowedRasterImageLayersForAnalysisType(
  analysisType: string | null | undefined,
): Set<ImageLayerKey> | null {
  switch (analysisType) {
    case 'aorticValveCalcium':
      return new Set<ImageLayerKey>(['base', 'aorta', 'avc']);
    case 'plaque':
      return new Set<ImageLayerKey>([
        'base',
        'lumen',
        'lumenoutline',
        'plaque',
        'plaqueoutline',
      ]);
    case 'stenosis':
    case 'isr':
      return new Set<ImageLayerKey>(['base', 'lumen', 'lumenoutline']);
    case 'fai':
      return new Set<ImageLayerKey>(['base', 'lumen', 'lumenoutline', 'fai']);
    default:
      return null;
  }
}

export function filterImageLayerVisibilityForAnalysisType(
  analysisType: string | null | undefined,
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
): Partial<Record<ImageLayerKey, boolean>> {
  const allowedLayers = getAllowedRasterImageLayersForAnalysisType(analysisType);
  if (!allowedLayers) return visibility ?? {};

  const next: Partial<Record<ImageLayerKey, boolean>> = {
    ...(visibility ?? {}),
  };
  RASTER_OVERLAY_LAYER_KEYS.forEach((layer) => {
    if (!allowedLayers.has(layer)) {
      next[layer] = false;
    }
  });
  return next;
}

export function resolveCprImageResourceModeForAnalysisType({
  analysisType,
  requestedMode,
  cpr,
}: {
  analysisType: string | null | undefined;
  requestedMode: CprMode | null | undefined;
  cpr?: Partial<Record<CprMode, CprImageResource>>;
}): CprMode | null {
  if (!requestedMode) return null;
  if (analysisType === 'aorticValveCalcium') {
    if (requestedMode !== 'avc' && cpr?.[requestedMode]) return requestedMode;
    if (cpr?.straightened) return 'straightened';
    if (cpr?.[requestedMode]) return requestedMode;
    return null;
  }
  return requestedMode;
}

export type CrossSectionImageAsset = {
  assetId: string;
  assetLayout?: 'direct-frame' | 'tiled-page' | 'branch-volume-pack' | string;
  branchId?: number | string;
  source?: string;
  width: number;
  height: number;
  rawWidth?: number;
  rawHeight?: number;
  displayScale?: number;
  displayWidth?: number;
  displayHeight?: number;
  basePath: string;
  labelPath?: string;
  mappingPath?: string;
  pageIndex?: number;
  tileIndex?: number;
  tileRect?: ImageTileRect;
  fieldOfViewMm?: number;
  spacingMm?: number;
  sourceSpacingMm?: number;
  samplingSpacingMm?: number;
  frameCount?: number;
  baseDtype?: string;
  labelDtype?: string;
  huMin?: number;
  huMax?: number;
  compression?: string;
  compressionLevel?: number;
  frameStrideBytes?: number;
  uncompressedBaseBytes?: number;
  uncompressedLabelBytes?: number;
  labelSmoothing?: unknown;
  labelValueHistogram?: Record<string, number>;
  clientIndex?: number;
  sourceCenterlineIndex?: number;
  localIndex?: number;
  world?: [number, number, number];
  normal?: [number, number, number];
  tangent?: [number, number, number];
  binormal?: [number, number, number];
  resliceAxes?: number[][];
};

export type CrossSectionFrameRef = {
  assetId: string;
  clientIndex?: number;
  localIndex?: number;
  sourceCenterlineIndex?: number;
  world?: [number, number, number];
  normal?: [number, number, number];
  tangent?: [number, number, number];
  binormal?: [number, number, number];
  resliceAxes?: number[][];
};

export type CrossSectionStackResource = {
  width: number;
  height: number;
  rawWidth?: number;
  rawHeight?: number;
  displayScale?: number;
  displayWidth?: number;
  displayHeight?: number;
  fieldOfViewMm?: number;
  spacingMm?: number;
  frameCount?: number;
  indexStart: number;
  indexEnd: number;
  indexPathTemplate?: string;
  layers?: Partial<Record<Exclude<ImageLayerKey, 'base'>, string>>;
  indexMapPath?: string;
  assetManifestPath?: string;
  labelLayers?: ImageLayerLabelValues;
  assetByIndex?: Record<number, string>;
  frameByIndex?: Record<number, CrossSectionFrameRef>;
  assets?: Record<string, CrossSectionImageAsset>;
};

export type CprCrossSectionImages = {
  revision: string;
  displayImage?: {
    windowCenter?: number;
    windowWidth?: number;
    huMin?: number;
    huMax?: number;
  };
  branches: Record<
    string,
    {
      name?: string;
      source?: string;
      manifestPath?: string;
      loaded?: boolean;
      labelLayers?: ImageLayerLabelValues;
      cpr: Partial<Record<CprMode, CprImageResource>>;
      crossSection: CrossSectionStackResource;
    }
  >;
};

export type ResolvedImageLayerPaths = Partial<Record<ImageLayerKey, string>> & {
  base: string;
};

export const DEFAULT_CPR_CROSS_SECTION_IMAGE_MANIFEST_TARGET =
  'cpr_cross_section_images%2Fmanifest';

export const SERVER_LABEL_OVERLAY_PREFIX = 'server-label://';
export const SERVER_CPR_BASE_IMAGE_PREFIX = 'server-cpr-base://';
export const SERVER_TILE_IMAGE_PREFIX = 'server-tile://';
export const SERVER_VOLUME_SLICE_PREFIX = 'server-volume-slice://';
export const SERVER_VOLUME_LABEL_PREFIX = 'server-volume-label://';
export const SERVER_FRONT_DATA_CPR_BASE_PREFIX = 'server-front-data-cpr-base://';
export const SERVER_FRONT_DATA_LABEL_PREFIX = 'server-front-data-label://';

export function isServerLabelOverlayPath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_LABEL_OVERLAY_PREFIX);
}

export function isServerCprBaseImagePath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_CPR_BASE_IMAGE_PREFIX);
}

export function isServerVolumeSlicePath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_VOLUME_SLICE_PREFIX);
}

export function isServerVolumeLabelPath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_VOLUME_LABEL_PREFIX);
}

export function isServerFrontDataCprBaseImagePath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_FRONT_DATA_CPR_BASE_PREFIX);
}

export function isServerFrontDataLabelOverlayPath(path: string | undefined) {
  return !!path && path.startsWith(SERVER_FRONT_DATA_LABEL_PREFIX);
}

export function createServerLabelOverlayPath(
  labelPath: string | undefined,
  layer: ImageLayerKey,
  transform?: ImageLayerTransform,
  values?: number[],
): string | undefined {
  if (!labelPath || layer === 'base') return undefined;
  const base = `${SERVER_LABEL_OVERLAY_PREFIX}${layer}/${encodeURIComponent(labelPath)}`;
  const params = new URLSearchParams();
  if (transform?.flipY) params.set('flipY', '1');
  if (transform?.outputWidth) params.set('outputWidth', String(transform.outputWidth));
  if (transform?.outputHeight) params.set('outputHeight', String(transform.outputHeight));
  if (transform?.compression) params.set('compression', transform.compression);
  if (transform?.uncompressedBytes) {
    params.set('uncompressedBytes', String(transform.uncompressedBytes));
  }
  if (values?.length) {
    params.set('values', values.map((value) => String(value)).join(','));
  }
  if (transform?.tileRect) {
    params.set('tileX', String(transform.tileRect.x));
    params.set('tileY', String(transform.tileRect.y));
    params.set('tileWidth', String(transform.tileRect.width));
    params.set('tileHeight', String(transform.tileRect.height));
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function createServerCprBaseImagePath(
  sourcePath: string | undefined,
  transform: CprBaseImageTransform,
): string | undefined {
  if (!sourcePath) return undefined;
  const base = `${SERVER_CPR_BASE_IMAGE_PREFIX}${encodeURIComponent(sourcePath)}`;
  const params = new URLSearchParams();
  if (transform.outputWidth) params.set('outputWidth', String(transform.outputWidth));
  if (transform.outputHeight) params.set('outputHeight', String(transform.outputHeight));
  if (transform.compression) params.set('compression', transform.compression);
  if (transform.uncompressedBytes) {
    params.set('uncompressedBytes', String(transform.uncompressedBytes));
  }
  if (transform.dtype) params.set('dtype', transform.dtype);
  if (Number.isFinite(transform.huMin)) params.set('huMin', String(transform.huMin));
  if (Number.isFinite(transform.huMax)) params.set('huMax', String(transform.huMax));
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

function appendFrontDataImageTransformQuery(
  base: string,
  transform?: ImageLayerTransform | CprBaseImageTransform,
  values?: number[],
) {
  const params = new URLSearchParams();
  if (transform?.outputWidth) params.set('outputWidth', String(transform.outputWidth));
  if (transform?.outputHeight) params.set('outputHeight', String(transform.outputHeight));
  if (transform?.flipY) params.set('flipY', '1');
  if (transform?.compression) params.set('compression', transform.compression);
  if (transform?.uncompressedBytes) {
    params.set('uncompressedBytes', String(transform.uncompressedBytes));
  }
  const baseTransform = transform as CprBaseImageTransform | undefined;
  if (baseTransform?.dtype) params.set('dtype', baseTransform.dtype);
  if (Number.isFinite(baseTransform?.huMin)) {
    params.set('huMin', String(baseTransform?.huMin));
  }
  if (Number.isFinite(baseTransform?.huMax)) {
    params.set('huMax', String(baseTransform?.huMax));
  }
  if (values?.length) {
    params.set('values', values.map((value) => String(value)).join(','));
  }
  if (transform?.tileRect) {
    params.set('tileX', String(transform.tileRect.x));
    params.set('tileY', String(transform.tileRect.y));
    params.set('tileWidth', String(transform.tileRect.width));
    params.set('tileHeight', String(transform.tileRect.height));
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function createServerFrontDataCprBaseImagePath(
  zipPath: string | undefined,
  entryPath: string | undefined,
  transform: CprBaseImageTransform,
): string | undefined {
  if (!zipPath || !entryPath) return undefined;
  const base = `${SERVER_FRONT_DATA_CPR_BASE_PREFIX}${encodeURIComponent(zipPath)}/${encodeURIComponent(entryPath)}`;
  return appendFrontDataImageTransformQuery(base, transform);
}

export function createServerFrontDataLabelOverlayPath(
  zipPath: string | undefined,
  entryPath: string | undefined,
  layer: ImageLayerKey,
  transform?: ImageLayerTransform,
  values?: number[],
): string | undefined {
  if (!zipPath || !entryPath || layer === 'base') return undefined;
  const base = `${SERVER_FRONT_DATA_LABEL_PREFIX}${layer}/${encodeURIComponent(zipPath)}/${encodeURIComponent(entryPath)}`;
  return appendFrontDataImageTransformQuery(base, transform, values);
}

export function makeServerFrontDataLabelOverlayLayers(
  zipPath: string | undefined,
  entryPath: string | undefined,
  transform?: ImageLayerTransform,
  labelLayers?: ImageLayerLabelValues,
): Partial<Record<Exclude<ImageLayerKey, 'base'>, string>> {
  if (!zipPath || !entryPath) return {};
  const explicitLayers = !!labelLayers && Object.keys(labelLayers).length > 0;
  const valuesForLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    if (layer === 'lumenoutline') return labelLayers?.lumenoutline ?? labelLayers?.lumen;
    if (layer === 'plaqueoutline') return labelLayers?.plaqueoutline ?? labelLayers?.plaque;
    return labelLayers?.[layer];
  };
  const makeLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    const values = valuesForLayer(layer);
    if (explicitLayers && !values?.length) return undefined;
    return createServerFrontDataLabelOverlayPath(
      zipPath,
      entryPath,
      layer,
      transform,
      values,
    );
  };
  return {
    lumen: makeLayer('lumen'),
    plaque: makeLayer('plaque'),
    fai: makeLayer('fai'),
    aorta: makeLayer('aorta'),
    avc: makeLayer('avc'),
  };
}

function addBranchVolumeSliceQuery(
  base: string,
  transform: BranchVolumeSliceTransform,
  values?: number[],
) {
  const params = new URLSearchParams();
  params.set('frameIndex', String(transform.frameIndex));
  params.set('width', String(transform.width));
  params.set('height', String(transform.height));
  params.set('frameStrideBytes', String(transform.frameStrideBytes));
  if (transform.compression) params.set('compression', transform.compression);
  if (transform.uncompressedBytes) {
    params.set('uncompressedBytes', String(transform.uncompressedBytes));
  }
  if (transform.dtype) params.set('dtype', transform.dtype);
  if (Number.isFinite(transform.huMin)) params.set('huMin', String(transform.huMin));
  if (Number.isFinite(transform.huMax)) params.set('huMax', String(transform.huMax));
  if (values?.length) {
    params.set('values', values.map((value) => String(value)).join(','));
  }
  return `${base}?${params.toString()}`;
}

export function createServerVolumeSliceImagePath(
  volumePath: string | undefined,
  transform: BranchVolumeSliceTransform,
) {
  if (!volumePath) return undefined;
  return addBranchVolumeSliceQuery(
    `${SERVER_VOLUME_SLICE_PREFIX}base/${encodeURIComponent(volumePath)}`,
    transform,
  );
}

export function createServerVolumeLabelOverlayPath(
  volumePath: string | undefined,
  layer: ImageLayerKey,
  transform: BranchVolumeSliceTransform,
  values?: number[],
) {
  if (!volumePath || layer === 'base') return undefined;
  return addBranchVolumeSliceQuery(
    `${SERVER_VOLUME_LABEL_PREFIX}${layer}/${encodeURIComponent(volumePath)}`,
    transform,
    values,
  );
}

export function makeServerVolumeLabelOverlayLayers(
  volumePath: string | undefined,
  transform: BranchVolumeSliceTransform,
  labelLayers?: ImageLayerLabelValues,
): Partial<Record<Exclude<ImageLayerKey, 'base'>, string>> {
  if (!volumePath) return {};
  const explicitLayers = !!labelLayers && Object.keys(labelLayers).length > 0;
  const valuesForLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    if (layer === 'lumenoutline') return labelLayers?.lumenoutline ?? labelLayers?.lumen;
    if (layer === 'plaqueoutline') return labelLayers?.plaqueoutline ?? labelLayers?.plaque;
    return labelLayers?.[layer];
  };
  const makeLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    const values = valuesForLayer(layer);
    if (explicitLayers && !values?.length) return undefined;
    return createServerVolumeLabelOverlayPath(volumePath, layer, transform, values);
  };
  return {
    lumen: makeLayer('lumen'),
    lumenoutline: makeLayer('lumenoutline'),
    plaque: makeLayer('plaque'),
    plaqueoutline: makeLayer('plaqueoutline'),
    fai: makeLayer('fai'),
    aorta: makeLayer('aorta'),
    avc: makeLayer('avc'),
  };
}

export function makeServerLabelOverlayLayers(
  labelPath: string | undefined,
  transform?: ImageLayerTransform,
  labelLayers?: ImageLayerLabelValues,
): Partial<Record<Exclude<ImageLayerKey, 'base'>, string>> {
  if (!labelPath) return {};
  const explicitLayers = !!labelLayers && Object.keys(labelLayers).length > 0;
  const valuesForLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    if (layer === 'lumenoutline') return labelLayers?.lumenoutline ?? labelLayers?.lumen;
    if (layer === 'plaqueoutline') return labelLayers?.plaqueoutline ?? labelLayers?.plaque;
    return labelLayers?.[layer];
  };
  const makeLayer = (layer: Exclude<ImageLayerKey, 'base'>) => {
    const values = valuesForLayer(layer);
    if (explicitLayers && !values?.length) return undefined;
    return createServerLabelOverlayPath(labelPath, layer, transform, values);
  };
  return {
    lumen: makeLayer('lumen'),
    lumenoutline: makeLayer('lumenoutline'),
    plaque: makeLayer('plaque'),
    plaqueoutline: makeLayer('plaqueoutline'),
    fai: makeLayer('fai'),
    aorta: makeLayer('aorta'),
    avc: makeLayer('avc'),
  };
}

function addImageTransformQuery(path: string, transform: ImageLayerTransform) {
  const params = new URLSearchParams();
  if (transform.outputWidth) params.set('outputWidth', String(transform.outputWidth));
  if (transform.outputHeight) params.set('outputHeight', String(transform.outputHeight));
  if (transform.tileRect) {
    params.set('tileX', String(transform.tileRect.x));
    params.set('tileY', String(transform.tileRect.y));
    params.set('tileWidth', String(transform.tileRect.width));
    params.set('tileHeight', String(transform.tileRect.height));
  }
  const query = params.toString();
  if (!query) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
}

export function createServerTileImagePath(
  sourcePath: string | undefined,
  transform: ImageLayerTransform,
): string | undefined {
  if (!sourcePath || !transform.tileRect) return sourcePath;
  const encoded = encodeURIComponent(sourcePath);
  return addImageTransformQuery(`${SERVER_TILE_IMAGE_PREFIX}${encoded}`, transform);
}

export function normalizeCprCrossSectionImageMode(
  mode: unknown,
): CprCrossSectionImageMode {
  return mode === 'server' || mode === 'legacy' ? mode : 'legacy';
}

export function isCprCrossSectionImageModeEnabled(
  mode: CprCrossSectionImageMode,
) {
  return mode === 'server';
}

export function estimateGrayscaleImageBytes(
  width: number,
  height: number,
  frameCount = 1,
  layerCount = 1,
) {
  return (
    Math.max(0, Math.floor(width)) *
    Math.max(0, Math.floor(height)) *
    Math.max(0, Math.floor(frameCount)) *
    Math.max(0, Math.floor(layerCount))
  );
}

export function estimateDecodedImageBytes(
  width: number,
  height: number,
  frameCount = 1,
  layerCount = 1,
) {
  return estimateGrayscaleImageBytes(width, height, frameCount, layerCount) * 4;
}

export const DEFAULT_IMAGE_PREFETCH_DECODED_BUDGET_BYTES =
  512 * 1024 * 1024;
export const DEFAULT_CPR_PREFETCH_RADIUS = 36;
export const DEFAULT_CROSS_SECTION_PREFETCH_RADIUS = 8;

export type AdaptiveImagePrefetchStrategy = 'all' | 'radius';

export type AdaptiveImagePrefetchPlan = {
  layers: Array<ResolvedImageLayerPaths | null>;
  strategy: AdaptiveImagePrefetchStrategy;
  estimatedDecodedBytes: number;
  budgetBytes: number;
  radius: number;
  candidateCount: number;
  selectedCount: number;
  visibleLayerCount: number;
  orderedIndexes: number[];
};

type MockCprCrossSectionConfig = {
  coronary_front_expand: number;
  coronary_back_expand: number;
  stretchedAngle: number;
  stretchedCount: number;
  straightenedAngle: number;
  straightenedCount: number;
};

type MockManifestInput = {
  seriesId: string;
  centerlinePointCounts: Record<string | number, number>;
  centerlinePoints?: Record<string | number, MockCenterlinePoint[]>;
  branchIds?: Array<string | number>;
  defaultPointCount?: number;
  revisionSignature?: string;
  config: MockCprCrossSectionConfig;
  crossSectionSize?: number;
  untrimmedCrossSectionBranchIds?: Array<string | number>;
  straightenedWidthByBranchId?: Record<string | number, number>;
  cprFrameGeometryProvider?: (
    input: CprFrameGeometryProviderInput,
  ) => CprFrameGeometry | null | undefined;
};

function buildAngles(step: number, count: number) {
  const safeStep = Number.isFinite(step) && step > 0 ? step : 30;
  const safeCount =
    Number.isFinite(count) && count > 0 ? Math.floor(count) : 12;
  return Array.from({ length: safeCount }, (_v, index) =>
    normalizeAngleDeg(index * safeStep),
  );
}

function makeMockLayerPaths(prefix: string): ImageLayerPaths {
  return {
    base: `${prefix}/base`,
    lumen: `${prefix}/lumen`,
    lumenoutline: `${prefix}/lumenoutline`,
    plaque: `${prefix}/plaque`,
    plaqueoutline: `${prefix}/plaqueoutline`,
    fai: `${prefix}/fai`,
    aorta: `${prefix}/aorta`,
    avc: `${prefix}/avc`,
  };
}

export function buildMockCenterlinePath({
  indexStart,
  indexEnd,
  mode,
  width,
  height,
  angleDeg,
  centerlinePoints,
}: {
  indexStart: number;
  indexEnd: number;
  mode: CprMode;
  width: number;
  height: number;
  angleDeg: number;
  centerlinePoints?: MockCenterlinePoint[];
}): CprCenterlinePathPoint[] {
  const count = Math.max(1, indexEnd - indexStart + 1);
  if (
    mode === 'stretched' &&
    centerlinePoints &&
    centerlinePoints.length >= count
  ) {
    return buildMockStretchedPathFromCenterlinePoints({
      centerlinePoints: centerlinePoints.slice(indexStart, indexEnd + 1),
      indexStart,
      width,
      height,
      angleDeg,
    });
  }
  const angleRad = (normalizeAngleDeg(angleDeg) * Math.PI) / 180;
  const amplitude = Math.min(width * 0.08, 16);

  return Array.from({ length: count }, (_v, offset) => {
    const t = count === 1 ? 0 : offset / (count - 1);
    return {
      index: indexStart + offset,
      x:
        mode === 'straightened'
          ? width * 0.5
          : width * 0.5 + Math.sin(angleRad + t * Math.PI * 2) * amplitude,
      y: t * Math.max(0, height - 1),
    };
  });
}

function normalizeVec3(value: MockCenterlinePoint, fallback: MockCenterlinePoint) {
  const len = Math.hypot(value[0], value[1], value[2]);
  if (!Number.isFinite(len) || len < 1e-6) return fallback;
  return [value[0] / len, value[1] / len, value[2] / len] as MockCenterlinePoint;
}

function crossVec3(
  a: MockCenterlinePoint,
  b: MockCenterlinePoint,
): MockCenterlinePoint {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function subtractVec3(
  a: MockCenterlinePoint,
  b: MockCenterlinePoint,
): MockCenterlinePoint {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dotVec3(a: MockCenterlinePoint, b: MockCenterlinePoint) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function buildMockStretchedPathFromCenterlinePoints({
  centerlinePoints,
  indexStart,
  width,
  height,
  angleDeg,
}: {
  centerlinePoints: MockCenterlinePoint[];
  indexStart: number;
  width: number;
  height: number;
  angleDeg: number;
}): CprCenterlinePathPoint[] {
  const count = centerlinePoints.length;
  const first = centerlinePoints[0];
  const last = centerlinePoints[count - 1];
  const centerPoint = centerlinePoints[Math.floor(count / 2)];
  const axis = normalizeVec3(subtractVec3(last, first), [1, 0, 0]);
  const upRef =
    Math.abs(dotVec3(axis, [0, 0, 1])) > 0.9
      ? ([0, 1, 0] as MockCenterlinePoint)
      : ([0, 0, 1] as MockCenterlinePoint);
  const e0 = normalizeVec3(crossVec3(axis, upRef), [1, 0, 0]);
  const e1 = normalizeVec3(crossVec3(axis, e0), [0, 1, 0]);
  const angleRad = (normalizeAngleDeg(angleDeg) * Math.PI) / 180;
  const lateralAxis = normalizeVec3(
    [
      e0[0] * Math.cos(angleRad) + e1[0] * Math.sin(angleRad),
      e0[1] * Math.cos(angleRad) + e1[1] * Math.sin(angleRad),
      e0[2] * Math.cos(angleRad) + e1[2] * Math.sin(angleRad),
    ],
    e0,
  );
  const cumulative = [0];
  for (let i = 1; i < count; i += 1) {
    cumulative[i] =
      cumulative[i - 1] +
      Math.hypot(
        centerlinePoints[i][0] - centerlinePoints[i - 1][0],
        centerlinePoints[i][1] - centerlinePoints[i - 1][1],
        centerlinePoints[i][2] - centerlinePoints[i - 1][2],
      );
  }
  const totalDistance = cumulative[count - 1] || count - 1 || 1;
  const rawLateral = centerlinePoints.map((point) =>
    dotVec3(subtractVec3(point, centerPoint), lateralAxis),
  );
  const maxAbsLateral =
    Math.max(...rawLateral.map((value) => Math.abs(value))) || 1;
  const lateralScale = Math.min(1, (width * 0.38) / maxAbsLateral);

  return centerlinePoints.map((point, offset) => ({
    index: indexStart + offset,
    x: width * 0.5 + rawLateral[offset] * lateralScale,
    y: (cumulative[offset] / totalDistance) * Math.max(0, height - 1),
    position: point,
  }));
}

function buildDefaultCprViewBox(width: number, height: number): CprFrameViewBox {
  return {
    x: 0,
    y: 0,
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}

export function buildMockOverlayGeometry({
  centerlinePath,
  width,
}: {
  centerlinePath: CprCenterlinePathPoint[];
  width: number;
}): CprOverlayGeometry {
  void width;
  const bandWidth = 20;
  return {
    origin: 'top-left',
    coordinateSpace: 'image',
    perpendicularLines: centerlinePath.map((base, index) => {
      const prev = centerlinePath[Math.max(0, index - 1)] ?? base;
      const next =
        centerlinePath[Math.min(centerlinePath.length - 1, index + 1)] ?? base;
      const perpY = next.x - prev.x;
      const perpX = -(next.y - prev.y);
      const norm = Math.hypot(perpX, perpY) || 1;
      const unitX = (perpX / norm) * bandWidth;
      const unitY = (perpY / norm) * bandWidth;

      return {
        index: base.index,
        start: {
          x: base.x - unitX,
          y: base.y - unitY,
        },
        end: {
          x: base.x + unitX,
          y: base.y + unitY,
        },
      };
    }),
  };
}

export function buildMockCprCrossSectionImages({
  seriesId,
  centerlinePointCounts,
  centerlinePoints,
  branchIds,
  defaultPointCount,
  revisionSignature,
  config,
  crossSectionSize = 256,
  untrimmedCrossSectionBranchIds,
  straightenedWidthByBranchId,
  cprFrameGeometryProvider,
}: MockManifestInput): CprCrossSectionImages {
  const untrimmedBranchKeys = new Set(
    (untrimmedCrossSectionBranchIds ?? []).map((branchId) => String(branchId)),
  );
  const normalizedPointCounts = { ...centerlinePointCounts };
  Object.entries(centerlinePoints ?? {}).forEach(([branchId, points]) => {
    if (
      normalizedPointCounts[branchId] === undefined &&
      Array.isArray(points) &&
      points.length > 0
    ) {
      normalizedPointCounts[branchId] = points.length;
    }
  });
  (branchIds ?? []).forEach((branchId) => {
    const key = String(branchId);
    if (
      normalizedPointCounts[key] === undefined &&
      typeof defaultPointCount === 'number' &&
      Number.isFinite(defaultPointCount)
    ) {
      normalizedPointCounts[key] = defaultPointCount;
    }
  });

  const branches = Object.fromEntries(
    Object.entries(normalizedPointCounts)
      .map(([branchId, rawPointCount]) => {
        const pointCount = Math.max(1, Math.floor(Number(rawPointCount) || 0));
        const useFullCrossSectionRange = untrimmedBranchKeys.has(
          String(branchId),
        );
        const indexStart = useFullCrossSectionRange
          ? 0
          : Math.min(
              pointCount - 1,
              Math.max(0, Math.floor(config.coronary_front_expand ?? 0)),
            );
        const indexEnd = useFullCrossSectionRange
          ? pointCount - 1
          : Math.max(
              indexStart,
              pointCount -
                1 -
                Math.max(0, Math.floor(config.coronary_back_expand ?? 0)),
            );
        const cprHeight = pointCount;
        const branchCenterlinePoints = centerlinePoints?.[branchId];
        const modes: CprMode[] = ['straightened', 'stretched'];
        const cpr = Object.fromEntries(
          modes.map((mode) => {
            const width =
              mode === 'stretched'
                ? 300
                : Math.max(
                    1,
                    Math.floor(
                      Number(straightenedWidthByBranchId?.[branchId]) || 100,
                    ),
                  );
            const step =
              mode === 'stretched'
                ? config.stretchedAngle
                : config.straightenedAngle;
            const count =
              mode === 'stretched'
                ? config.stretchedCount
                : config.straightenedCount;
            const angleFrames = buildAngles(step, count).map((angleDeg) => {
              const providerGeometry = cprFrameGeometryProvider?.({
                branchId,
                mode,
                width,
                height: cprHeight,
                angleDeg,
                pointCount,
                centerlinePoints: branchCenterlinePoints,
              });
              const frameWidth = Math.max(
                1,
                Math.floor(providerGeometry?.width ?? width),
              );
              const frameHeight = Math.max(
                1,
                Math.floor(providerGeometry?.height ?? cprHeight),
              );
              const centerlinePath = providerGeometry?.centerlinePath?.length
                ? providerGeometry.centerlinePath
                : buildMockCenterlinePath({
                    indexStart: 0,
                    indexEnd: pointCount - 1,
                    mode,
                    width: frameWidth,
                    height: frameHeight,
                    angleDeg,
                    centerlinePoints: branchCenterlinePoints,
                  });
              return {
                angleDeg,
                width: frameWidth,
                height: frameHeight,
                layers: makeMockLayerPaths(
                  `mock://cpr/${seriesId}/${branchId}/${mode}/${angleDeg}`,
                ),
                centerlinePath,
                defaultViewBox:
                  providerGeometry?.defaultViewBox ??
                  buildDefaultCprViewBox(frameWidth, frameHeight),
                overlayGeometry:
                  providerGeometry?.overlayGeometry ??
                  buildMockOverlayGeometry({
                    centerlinePath,
                    width: frameWidth,
                  }),
              };
            });
            return [
              mode,
              {
                mode,
                angleFrames,
              },
            ];
          }),
        ) as Record<CprMode, CprImageResource>;

        return [
          branchId,
          {
            cpr,
            crossSection: {
              width: crossSectionSize,
              height: crossSectionSize,
              indexStart,
              indexEnd,
              indexPathTemplate: `mock://cross-section/${seriesId}/${branchId}/{index}/base`,
              layers: {
                lumen: `mock://cross-section/${seriesId}/${branchId}/{index}/lumen`,
                lumenoutline: `mock://cross-section/${seriesId}/${branchId}/{index}/lumenoutline`,
                plaque: `mock://cross-section/${seriesId}/${branchId}/{index}/plaque`,
                plaqueoutline: `mock://cross-section/${seriesId}/${branchId}/{index}/plaqueoutline`,
                fai: `mock://cross-section/${seriesId}/${branchId}/{index}/fai`,
                aorta: `mock://cross-section/${seriesId}/${branchId}/{index}/aorta`,
                avc: `mock://cross-section/${seriesId}/${branchId}/{index}/avc`,
              },
            },
          },
        ];
      })
      .filter((entry) => entry[0] !== ''),
  ) as CprCrossSectionImages['branches'];

  return {
    revision: revisionSignature
      ? `mock-${seriesId}-${revisionSignature}`
      : `mock-${seriesId}`,
    branches,
  };
}

const MANIFEST_PARAMETER_PATHS = [
  ['cprCrossSectionImages'],
  ['CprCrossSectionImages'],
  ['CPRCrossSectionImages'],
  ['cpr_cross_section_images'],
  ['SavedInfo', 'cprCrossSectionImages'],
  ['SavedInfo', 'CprCrossSectionImages'],
  ['SavedInfo', 'CPRCrossSectionImages'],
  ['SavedInfo', 'cpr_cross_section_images'],
  ['SavedInfo', 'ImageResources', 'cprCrossSectionImages'],
  ['SavedInfo', 'Interpretation', 'cprCrossSectionImages'],
] as const;

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

export function isCprCrossSectionImages(
  value: unknown,
): value is CprCrossSectionImages {
  return (
    isObject(value) &&
    typeof value.revision === 'string' &&
    isObject(value.branches)
  );
}

export function getCprCrossSectionImagesFromParameter(
  parameter: unknown,
): CprCrossSectionImages | null {
  if (isCprCrossSectionImages(parameter)) return parameter;
  if (!isObject(parameter)) return null;

  for (const path of MANIFEST_PARAMETER_PATHS) {
    const found = path.reduce<unknown>(
      (acc, key) => (isObject(acc) ? acc[key] : undefined),
      parameter,
    );
    if (isCprCrossSectionImages(found)) return found;
  }

  return null;
}

export function getBranchImageResource(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
) {
  if (!manifest || branchId === null || branchId === undefined) return null;
  const branchKey = String(branchId);
  return manifest.branches[branchKey] ?? manifest.branches.aorta ?? null;
}

export function normalizeAngleDeg(angleDeg: number): number {
  if (!Number.isFinite(angleDeg)) return 0;
  return ((angleDeg % 360) + 360) % 360;
}

function circularAngleDistance(a: number, b: number): number {
  const diff = Math.abs(normalizeAngleDeg(a) - normalizeAngleDeg(b));
  return Math.min(diff, 360 - diff);
}

export function selectNearestCprAngleFrame(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
  mode: CprMode | null | undefined,
  angleDeg: number,
): CprAngleFrame | null {
  if (!mode) return null;
  const resource = getBranchImageResource(manifest, branchId)?.cpr?.[mode];
  const frames = resource?.angleFrames ?? [];
  if (!frames.length) return null;

  const normalized = normalizeAngleDeg(angleDeg);
  return frames.reduce<CprAngleFrame>(
    (best, frame) =>
      circularAngleDistance(frame.angleDeg, normalized) <
      circularAngleDistance(best.angleDeg, normalized)
        ? frame
        : best,
    frames[0],
  );
}

export function resolveCprFrameDefaultViewBox(
  frame: CprAngleFrame | null | undefined,
): CprFrameViewBox | undefined {
  if (!frame) return undefined;
  const candidate = frame.defaultViewBox;
  if (
    candidate &&
    Number.isFinite(candidate.x) &&
    Number.isFinite(candidate.y) &&
    Number.isFinite(candidate.width) &&
    Number.isFinite(candidate.height) &&
    candidate.width > 0 &&
    candidate.height > 0
  ) {
    return candidate;
  }
  return buildDefaultCprViewBox(frame.width, frame.height);
}

function formatIndexToken(index: number, width?: number): string {
  const text = String(index);
  if (!width || text.length >= width) return text;
  return text.padStart(width, '0');
}

export function formatIndexPathTemplate(
  template: string,
  index: number,
): string {
  if (!template) return template;
  return template
    .replace(/\{index:(\d+)d\}/g, (_match, width) =>
      formatIndexToken(index, Number(width)),
    )
    .replace(/\{index\}/g, String(index));
}

export function resolveServerImagePath(
  path: string | undefined,
  revision?: string,
): string | undefined {
  if (!path) return undefined;
  if (!revision || path.startsWith('data:') || path.startsWith('blob:')) {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}revision=${encodeURIComponent(revision)}`;
}

export function resolveImageLayersWithRevision(
  layers: Partial<Record<ImageLayerKey, string>> | undefined,
  revision?: string,
): ResolvedImageLayerPaths | null {
  const base = resolveServerImagePath(layers?.base, revision);
  if (!base) return null;
  return {
    base,
    lumen: resolveServerImagePath(layers?.lumen, revision),
    lumenoutline: resolveServerImagePath(layers?.lumenoutline, revision),
    plaque: resolveServerImagePath(layers?.plaque, revision),
    plaqueoutline: resolveServerImagePath(layers?.plaqueoutline, revision),
    fai: resolveServerImagePath(layers?.fai, revision),
    aorta: resolveServerImagePath(layers?.aorta, revision),
    avc: resolveServerImagePath(layers?.avc, revision),
  };
}

function bytesPerPixelForDtype(dtype: string | undefined) {
  const clean = (dtype ?? 'uint8').trim().toLowerCase();
  if (clean.includes('int16') || clean.includes('uint16') || clean === 'i16') {
    return 2;
  }
  if (clean.includes('float32') || clean === 'f32') return 4;
  return 1;
}

export function resolveCrossSectionLayerPaths(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
  index: number | null | undefined,
): ResolvedImageLayerPaths | null {
  const stack = getBranchImageResource(manifest, branchId)?.crossSection;
  if (!stack || !Number.isFinite(index)) return null;
  const safeIndex = Number(index);
  if (safeIndex < stack.indexStart || safeIndex > stack.indexEnd) return null;

  const frameRef = stack.frameByIndex?.[safeIndex];
  const assetId = frameRef?.assetId ?? stack.assetByIndex?.[safeIndex];
  const asset = assetId ? stack.assets?.[assetId] : undefined;
  if (asset?.basePath) {
    const outputWidth =
      asset.displayWidth ?? stack.displayWidth ?? asset.width ?? stack.width;
    const outputHeight =
      asset.displayHeight ?? stack.displayHeight ?? asset.height ?? stack.height;
    if (asset.assetLayout === 'branch-volume-pack') {
      const frameIndex = frameRef?.localIndex ?? asset.localIndex;
      const baseFrameStrideBytes =
        asset.frameStrideBytes ??
        asset.width * asset.height * bytesPerPixelForDtype(asset.baseDtype);
      const labelFrameStrideBytes =
        asset.frameStrideBytes ??
        asset.width * asset.height * bytesPerPixelForDtype(asset.labelDtype);
      if (typeof frameIndex !== 'number' || !Number.isFinite(frameIndex)) {
        return null;
      }
      const transform: BranchVolumeSliceTransform = {
        frameIndex,
        width: outputWidth,
        height: outputHeight,
        frameStrideBytes: baseFrameStrideBytes,
        compression: asset.compression,
        uncompressedBytes: asset.uncompressedBaseBytes,
        dtype: asset.baseDtype,
        huMin: asset.huMin,
        huMax: asset.huMax,
      };
      const labelTransform: BranchVolumeSliceTransform = {
        frameIndex,
        width: outputWidth,
        height: outputHeight,
        frameStrideBytes: labelFrameStrideBytes,
        compression: asset.compression,
        uncompressedBytes: asset.uncompressedLabelBytes,
      };
      const base = createServerVolumeSliceImagePath(asset.basePath, transform);
      if (!base) return null;
      return {
        base,
        ...makeServerVolumeLabelOverlayLayers(
          asset.labelPath,
          labelTransform,
          stack.labelLayers,
        ),
      };
    }
    const transform: ImageLayerTransform = {
      tileRect: asset.tileRect,
      outputWidth,
      outputHeight,
    };
    const base =
      asset.tileRect
        ? createServerTileImagePath(asset.basePath, transform)
        : asset.basePath;
    return {
      base: base ?? asset.basePath,
      ...makeServerLabelOverlayLayers(asset.labelPath, transform, stack.labelLayers),
    };
  }

  if (!stack.indexPathTemplate) return null;

  const base = formatIndexPathTemplate(stack.indexPathTemplate, safeIndex);
  const layers = Object.fromEntries(
    Object.entries(stack.layers ?? {}).map(([key, path]) => [
      key,
      formatIndexPathTemplate(path, safeIndex),
    ]),
  ) as Partial<Record<ImageLayerKey, string>>;

  return {
    base,
    ...layers,
  };
}

function visibleLayerCount(
  layers: Partial<Record<ImageLayerKey, string>> | null | undefined,
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
) {
  if (!layers?.base) return 0;
  return (
    1 +
    RASTER_OVERLAY_LAYER_KEYS.filter(
      (key) => !!layers[key] && visibility?.[key] !== false,
    ).length
  );
}

function emptyPrefetchPlan({
  budgetBytes,
  radius,
}: {
  budgetBytes: number;
  radius: number;
}): AdaptiveImagePrefetchPlan {
  return {
    layers: [],
    strategy: 'radius',
    estimatedDecodedBytes: 0,
    budgetBytes,
    radius,
    candidateCount: 0,
    selectedCount: 0,
    visibleLayerCount: 0,
    orderedIndexes: [],
  };
}

function orderCircularIndexes({
  count,
  activeIndex,
  includeAll,
  radius,
  direction = 0,
}: {
  count: number;
  activeIndex: number;
  includeAll: boolean;
  radius: number;
  direction?: number;
}) {
  if (count <= 1) return [];
  const center =
    Number.isFinite(activeIndex) && activeIndex >= 0 ? activeIndex : 0;
  const selected = new Set<number>();
  const ordered: number[] = [];
  const maxDistance = includeAll ? count - 1 : Math.max(0, radius);

  for (
    let distance = 1;
    distance <= maxDistance && ordered.length < count - 1;
    distance += 1
  ) {
    const candidates =
      direction < 0
        ? [center - distance, center + distance]
        : [center + distance, center - distance];

    candidates.forEach((candidate) => {
      const index = ((candidate % count) + count) % count;
      if (index === center || selected.has(index)) return;
      selected.add(index);
      ordered.push(index);
    });
  }

  return ordered;
}

function orderLinearIndexes({
  start,
  end,
  activeIndex,
  includeAll,
  radius,
}: {
  start: number;
  end: number;
  activeIndex: number;
  includeAll: boolean;
  radius: number;
}) {
  if (end < start) return [];
  const center = Math.max(start, Math.min(end, Math.floor(activeIndex)));
  const ordered: number[] = [];
  const maxDistance = includeAll ? end - start : Math.max(0, radius);

  for (let distance = 1; distance <= maxDistance; distance += 1) {
    const forward = center + distance;
    if (forward <= end) ordered.push(forward);

    const backward = center - distance;
    if (backward >= start) ordered.push(backward);
  }

  return ordered;
}

export function buildCprImagePrefetchPlan({
  resource,
  activeFrame,
  revision,
  visibility,
  budgetBytes = DEFAULT_IMAGE_PREFETCH_DECODED_BUDGET_BYTES,
  radius = DEFAULT_CPR_PREFETCH_RADIUS,
  direction = 0,
}: {
  resource: CprImageResource | null | undefined;
  activeFrame: CprAngleFrame | null | undefined;
  revision?: string;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  budgetBytes?: number;
  radius?: number;
  direction?: number;
}): AdaptiveImagePrefetchPlan {
  const safeBudget = Math.max(0, Math.floor(budgetBytes));
  const safeRadius = Math.max(0, Math.floor(radius));
  if (!resource?.angleFrames?.length || !activeFrame) {
    return emptyPrefetchPlan({ budgetBytes: safeBudget, radius: safeRadius });
  }

  const frames = resource.angleFrames;
  const estimatedDecodedBytes = frames.reduce(
    (total, frame) =>
      total +
      estimateDecodedImageBytes(
        frame.width,
        frame.height,
        1,
        visibleLayerCount(frame.layers, visibility),
      ),
    0,
  );
  const maxVisibleLayerCount = frames.reduce(
    (max, frame) => Math.max(max, visibleLayerCount(frame.layers, visibility)),
    0,
  );
  const strategy: AdaptiveImagePrefetchStrategy =
    estimatedDecodedBytes <= safeBudget ? 'all' : 'radius';
  const activeIndex = frames.findIndex((frame) => frame === activeFrame);
  const orderedIndexes = orderCircularIndexes({
    count: frames.length,
    activeIndex,
    includeAll: strategy === 'all',
    radius: safeRadius,
    direction,
  });
  const layers = orderedIndexes
    .map((index) => frames[index])
    .map((frame) => resolveImageLayersWithRevision(frame.layers, revision));

  return {
    layers,
    strategy,
    estimatedDecodedBytes,
    budgetBytes: safeBudget,
    radius: safeRadius,
    candidateCount: Math.max(0, frames.length - 1),
    selectedCount: layers.length,
    visibleLayerCount: maxVisibleLayerCount,
    orderedIndexes,
  };
}

export function buildCrossSectionImagePrefetchPlan({
  manifest,
  branchId,
  activeIndex,
  revision,
  visibility,
  budgetBytes = DEFAULT_IMAGE_PREFETCH_DECODED_BUDGET_BYTES,
  radius = DEFAULT_CROSS_SECTION_PREFETCH_RADIUS,
}: {
  manifest: CprCrossSectionImages | null | undefined;
  branchId: number | string | null | undefined;
  activeIndex: number | null | undefined;
  revision?: string;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  budgetBytes?: number;
  radius?: number;
}): AdaptiveImagePrefetchPlan {
  const safeBudget = Math.max(0, Math.floor(budgetBytes));
  const safeRadius = Math.max(0, Math.floor(radius));
  const stack = getBranchImageResource(manifest, branchId)?.crossSection;
  if (!stack || !Number.isFinite(activeIndex)) {
    return emptyPrefetchPlan({ budgetBytes: safeBudget, radius: safeRadius });
  }

  const start = Math.floor(stack.indexStart);
  const end = Math.floor(stack.indexEnd);
  if (end < start) {
    return emptyPrefetchPlan({ budgetBytes: safeBudget, radius: safeRadius });
  }

  const clampedActiveIndex = Math.max(
    start,
    Math.min(end, Math.floor(Number(activeIndex))),
  );
  const sampleLayers = resolveCrossSectionLayerPaths(
    manifest,
    branchId,
    clampedActiveIndex,
  );
  const visibleCount = visibleLayerCount(sampleLayers, visibility);
  const frameCount = end - start + 1;
  const estimatedDecodedBytes = estimateDecodedImageBytes(
    stack.width,
    stack.height,
    frameCount,
    visibleCount,
  );
  const strategy: AdaptiveImagePrefetchStrategy =
    estimatedDecodedBytes <= safeBudget ? 'all' : 'radius';
  const orderedIndexes = orderLinearIndexes({
    start,
    end,
    activeIndex: clampedActiveIndex,
    includeAll: strategy === 'all',
    radius: safeRadius,
  });
  const layers = orderedIndexes.map((index) =>
    resolveImageLayersWithRevision(
      resolveCrossSectionLayerPaths(manifest, branchId, index) ?? undefined,
      revision,
    ),
  );

  return {
    layers,
    strategy,
    estimatedDecodedBytes,
    budgetBytes: safeBudget,
    radius: safeRadius,
    candidateCount: Math.max(0, frameCount - 1),
    selectedCount: layers.length,
    visibleLayerCount: visibleCount,
    orderedIndexes,
  };
}

export function getCrossSectionImageAsset(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
  index: number | null | undefined,
): CrossSectionImageAsset | null {
  const stack = getBranchImageResource(manifest, branchId)?.crossSection;
  if (!stack || !Number.isFinite(index)) return null;
  const safeIndex = Number(index);
  const frameRef = stack.frameByIndex?.[safeIndex];
  const assetId = frameRef?.assetId ?? stack.assetByIndex?.[safeIndex];
  const asset = assetId ? stack.assets?.[assetId] : null;
  if (!asset) return null;
  return frameRef ? { ...asset, ...frameRef } : asset;
}

export function buildPickPointFromCrossSectionAsset(
  asset: CrossSectionImageAsset | null | undefined,
  index: number,
) {
  if (asset?.world && asset.normal) {
    return {
      index,
      position: asset.world,
      normal: asset.normal,
    };
  }
  return { index };
}

export function resolveCrossSectionLayerPathsWithRevision(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
  index: number | null | undefined,
): ResolvedImageLayerPaths | null {
  const resolved = resolveCrossSectionLayerPaths(manifest, branchId, index);
  return resolveImageLayersWithRevision(
    resolved ?? undefined,
    manifest?.revision,
  );
}

export function getVisibleImageLayerEntries(
  layers: ResolvedImageLayerPaths | null,
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
) {
  if (!layers) return [];
  const order: ImageLayerKey[] = [
    'base',
    'lumen',
    'lumenoutline',
    'plaque',
    'plaqueoutline',
    'fai',
    'aorta',
    'avc',
  ];
  return order
    .filter((key) => key === 'base' || visibility?.[key] !== false)
    .map((key) => [key, layers[key]] as const)
    .filter(
      (entry): entry is readonly [ImageLayerKey, string] =>
        typeof entry[1] === 'string' && entry[1].length > 0,
    );
}

export function findNearestCprCenterlinePathIndex(
  path: CprCenterlinePathPoint[] | null | undefined,
  point: { x: number; y: number },
): number | null {
  if (!path?.length) return null;
  let bestIndex: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  path.forEach((pathPoint) => {
    if (
      !Number.isFinite(pathPoint.index) ||
      !Number.isFinite(pathPoint.x) ||
      !Number.isFinite(pathPoint.y)
    ) {
      return;
    }
    const dx = pathPoint.x - point.x;
    const dy = pathPoint.y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = Number.isFinite(pathPoint.sourceCenterlineIndex)
        ? pathPoint.sourceCenterlineIndex!
        : pathPoint.index;
    }
  });

  return bestIndex;
}
